import {
  cacheImageDataUrl,
  convertBase64ToDataUrl
} from '@/services/tierImageService';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {isBlobTierImageUploadEnabled} from '@/utils/marketFeatures';

/**
 * Resolves the image value for the form from content data.
 * For blob flow: returns CDN URL string.
 * For legacy flow (Colombia): returns numeric image ID.
 */
const resolveImageValue = (imageObject, imageFallback, isBlobUpload) => {
  if (isBlobUpload) {
    if (imageObject?.cdnUrl) {
      return imageObject.cdnUrl;
    }
    if (typeof imageFallback === 'string' && imageFallback.startsWith('http')) {
      return imageFallback;
    }
    if (imageObject?.id) {
      return String(imageObject.id);
    }
    return imageFallback ? String(imageFallback) : '';
  }
  // Legacy flow: return numeric ID as-is
  if (imageObject?.id) {
    return imageObject.id;
  }
  return imageFallback || '';
};

/**
 * Caches image data for display.
 * Supports CDN URLs (new) and legacy base64 data.
 */
const cacheImageFromObject = (imageObject, imageFallback) => {
  // New CDN URL flow
  if (imageObject?.cdnUrl) {
    cacheImageDataUrl(
      imageObject.cdnUrl,
      imageObject.cdnUrl,
      imageObject.fileName || imageObject.originalFileName
    );
    return;
  }
  // If the image field is already a CDN URL
  if (typeof imageFallback === 'string' && imageFallback.startsWith('http')) {
    cacheImageDataUrl(imageFallback, imageFallback);
    return;
  }
  // Legacy base64 flow
  if (imageObject?.id && imageObject?.data) {
    const dataUrl = convertBase64ToDataUrl(imageObject.data);
    cacheImageDataUrl(imageObject.id, dataUrl);
  }
};

export function transformTierDataToForm(
  tierData,
  formatMessage,
  countryCode = 'MX'
) {
  const isBlobUpload = isBlobTierImageUploadEnabled(countryCode);
  const formData = {
    name: tierData.name || '',
    tierId: String(tierData.tierID || tierData.tierId || ''),
    activities:
      tierData.activities?.map((activity) => ({
        activityID: String(activity.activityID || ''),
        activityName: activity.activityName,
        multiplier: activity.multiplier
      })) || [],
    content: {}
  };

  const languages = COUNTRY_LANGUAGES_CODES[countryCode] || [];
  const expectedLanguages = languages.map((lang) => lang.code);

  expectedLanguages.forEach((langCode) => {
    formData.content[langCode] = {
      tierName: tierData.name || '',
      displayDescription: '',
      imageTierIcon: '',
      imageTierIconObject: null,
      bannerHeader: '',
      bannerDescription: '',
      bannerImage: '',
      bannerImageObject: null,
      detailsDescription: '',
      detailsTermsAndConditions: '',
      detailsImage: '',
      detailsImageObject: null
    };
  });

  if (tierData.content && Object.keys(tierData.content).length > 0) {
    Object.keys(tierData.content).forEach((langKey) => {
      if (!expectedLanguages.includes(langKey)) {
        return;
      }

      const content = tierData.content[langKey];
      cacheImageFromObject(content.imageTierIconObject, content.imageTierIcon);
      cacheImageFromObject(content.bannerImageObject, content.bannerImage);
      cacheImageFromObject(content.detailsImageObject, content.detailsImage);

      formData.content[langKey] = {
        tierName: content.tierName || tierData.name || '',
        displayDescription: content.displayDescription || '',
        imageTierIcon: resolveImageValue(
          content.imageTierIconObject,
          content.imageTierIcon,
          isBlobUpload
        ),
        imageTierIconObject: content.imageTierIconObject || null,
        bannerHeader: content.bannerHeader || '',
        bannerDescription: content.bannerDescription || '',
        bannerImage: resolveImageValue(
          content.bannerImageObject,
          content.bannerImage,
          isBlobUpload
        ),
        bannerImageObject: content.bannerImageObject || null,
        detailsDescription: content.detailsDescription || '',
        detailsTermsAndConditions: content.detailsTermsAndConditions || '',
        detailsImage: resolveImageValue(
          content.detailsImageObject,
          content.detailsImage,
          isBlobUpload
        ),
        detailsImageObject: content.detailsImageObject || null
      };
    });
  }

  return formData;
}
